/* eslint-disable indent */
import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { useCurrency } from "../../utils";
import { validarSumaCuotas } from "../../utils/cuotasUtil";
import { derivarClaveHardware } from "../../utils/lineasFacturacion/resolverLinea";
import { client } from "../../utils/supabase";
import styles from "./stylesFacturacionHw.module.scss";

// <FacturacionHardwareModal> — declara el SPLIT de facturación del hardware CAPEX de un
// negocio de cartelería en 2 tramos POR PORCENTAJE sobre `monto_hardware`, en `moneda_hardware`.
// Fuente única: `negocios_carteleria.detalle_pagos_hardware` (regla 10.1) para los campos que
// `lineas_facturacion` no trackea (porcentaje, fecha_factura_estimada, fecha_cobro_estimada, y el
// tramo aún sin fecha — L1 no deriva línea para un tramo no datable). L6 tramo 3: el candado "ya
// vinculado" se resuelve por `linea_id` contra el puente (`lineas_facturacion` +
// `facturas_sii_vinculaciones`), NUNCA por posición/`venta_ref_index`; el guardado de un tramo
// EDITABLE (aún sin factura real) va por `upsert_linea_facturacion` (crea o edita por clave
// natural, 10.1) — nunca se reconstruye el array completo por índice desde el cliente.

// Decimales por moneda: CLP entero; UF/USD/PEN con 2 (mismo criterio que CarteleriaForm).
const decimalesDe = (moneda) => ((moneda || "CLP").toUpperCase() === "CLP" ? 0 : 2);
const redondear = (v, dec) => {
  const f = 10 ** dec;
  return Math.round((Number(v) || 0) * f) / f;
};
const pad2 = (n) => String(n).padStart(2, "0");
// 'YYYY-MM-DD' → Date en horario LOCAL (evita el corrimiento UTC de `new Date('YYYY-MM-DD')`).
const parseFechaLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = String(str).substring(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
// Date → 'YYYY-MM-DD' local (sin toISOString, que desplaza por zona).
const fechaISOLocal = (date) =>
  date ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` : null;

const FacturacionHardwareModal = ({ negocio, onClose, onSaved }) => {
  const { formatAmount } = useCurrency();
  const { id: userId } = useAuthContext();

  const moneda = negocio.moneda_hardware || "CLP";
  const dec = decimalesDe(moneda);
  const montoTotal = parseFloat(negocio.monto_hardware) || 0;

  // Cada tramo: { pct (string), fecha (Date|null = facturafecha REAL), fechaEstimada (Date|null =
  // fecha_factura_estimada, se completa SOLO si el tramo aún no tiene factura real), locked, preserved }.
  const [tramos, setTramos] = useState([]);
  const [cargando, setCargando] = useState(true);
  // Aviso de incompatibilidad (10.177): si el detalle existente NO encaja en el modelo de 2 tramos
  // por %, NO se sobrescribe — se bloquea Guardar y se muestra el motivo.
  const [incompatible, setIncompatible] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Precarga: parsea el detalle existente + detecta tramos ya facturados (candado read-only).
  useEffect(() => {
    let vivo = true;
    const cargar = async () => {
      setCargando(true);
      setIncompatible(null);

      const detalle =
        typeof negocio.detalle_pagos_hardware === "string"
          ? JSON.parse(negocio.detalle_pagos_hardware || "[]")
          : negocio.detalle_pagos_hardware || [];

      // L6 tramo 3: el candado "ya vinculado" (locked, read-only) se resuelve por LÍNEA contra el
      // puente — nunca por posición (`venta_ref_index`). Cada tramo del array se identifica por su
      // clave natural (derivarClaveHardware, 10.1: mismo algoritmo que nc_lineas_derivadas), y esa
      // clave se busca en `lineas_facturacion` (tipo hardware de este negocio) + su puente en
      // `facturas_sii_vinculaciones`. Un tramo sin fecha no tiene clave → nunca puede estar vinculado.
      const { data: lineasHw, error } = await client
        .from("lineas_facturacion")
        .select("id, ano, mes, tramo, facturas_sii_vinculaciones(id)")
        .eq("negocio_id", String(negocio.id))
        .eq("tipo", "hardware")
        .order("ano")
        .order("mes")
        .order("tramo");
      if (!vivo) return;
      if (error) {
        // No tragarse el error (10.177): sin poder verificar los candados, bloquear Guardar.
        setIncompatible(`No se pudieron leer las líneas de facturación (${error.message}). No es seguro guardar.`);
        setTramos(
          detalle.map((e, i) => ({
            pct: e?.porcentaje != null ? String(e.porcentaje) : "",
            fecha: parseFechaLocal(e?.facturafecha || e?.fecha_factura),
            fechaEstimada: parseFechaLocal(e?.fecha_factura_estimada),
            locked: true,
            preserved: e,
            _idx: i,
          }))
        );
        setCargando(false);
        return;
      }
      // Mapa clave "YYYY-MM-tramo" → { linea_id, vinculado } para lookup O(1) por tramo del array.
      const lineaPorClave = new Map(
        (lineasHw || []).map((l) => [
          `${l.ano}-${l.mes}-${l.tramo}`,
          { linea_id: l.id, vinculado: (l.facturas_sii_vinculaciones || []).length > 0 },
        ])
      );
      const claveDeEntry = (i) => {
        const c = derivarClaveHardware(detalle, i);
        return c ? { key: `${c.ano}-${c.mes}-${c.tramo}`, ...c } : null;
      };
      const lineaDeEntry = (i) => {
        const c = claveDeEntry(i);
        return c ? lineaPorClave.get(c.key) || null : null;
      };
      const estaFacturado = (entry, i) => {
        if (entry && entry.facturanumero != null && String(entry.facturanumero).trim() !== "") return true;
        return !!lineaDeEntry(i)?.vinculado;
      };

      if (detalle.length === 0) {
        // Sin detalle → arranque fresco 75/25 (caso anticipo/contra-entrega típico), editable.
        setTramos([
          { pct: "75", fecha: null, fechaEstimada: null, locked: false, preserved: null },
          { pct: "25", fecha: null, fechaEstimada: null, locked: false, preserved: null },
        ]);
      } else if (detalle.length === 2 && detalle.every((e) => e && e.porcentaje != null)) {
        // Detalle propio del modelo (2 tramos con %) → precarga por índice (solo para leer porcentaje/
        // fechas del array, 10.1 — no hay escritura por índice). Locked si ya facturado; `linea_id`/
        // `clave` quedan guardados para el guardado por RPC (upsert_linea_facturacion por clave natural).
        setTramos(
          detalle.map((e, i) => ({
            pct: String(e.porcentaje),
            fecha: parseFechaLocal(e.facturafecha || e.fecha_factura),
            fechaEstimada: parseFechaLocal(e.fecha_factura_estimada),
            locked: estaFacturado(e, i),
            preserved: e,
            linea_id: lineaDeEntry(i)?.linea_id ?? null,
            clave: claveDeEntry(i),
          }))
        );
      } else {
        // Historial libre (cantidad ≠ 2, o entries sin "porcentaje") → NO sobrescribir. Aviso + bloqueo.
        setIncompatible(
          `El detalle de facturación de hardware existente (${detalle.length} entrada${
            detalle.length === 1 ? "" : "s"
          }) no encaja en el modelo de 2 tramos por porcentaje. Resolvelo a mano antes de declarar el split; no se sobrescribe automáticamente.`
        );
        setTramos(
          detalle.map((e, i) => ({
            pct: e?.porcentaje != null ? String(e.porcentaje) : "",
            fecha: parseFechaLocal(e?.facturafecha || e?.fecha_factura),
            fechaEstimada: parseFechaLocal(e?.fecha_factura_estimada),
            locked: true,
            preserved: e,
            _idx: i,
          }))
        );
      }
      setCargando(false);
    };
    cargar();
    return () => {
      vivo = false;
    };
  }, [negocio]);

  const setPct = (i, val) => setTramos((prev) => prev.map((t, idx) => (idx === i ? { ...t, pct: val } : t)));
  const setFecha = (i, val) => setTramos((prev) => prev.map((t, idx) => (idx === i ? { ...t, fecha: val } : t)));
  const setFechaEstimada = (i, val) =>
    setTramos((prev) => prev.map((t, idx) => (idx === i ? { ...t, fechaEstimada: val } : t)));

  // Materialización %→monto: los tramos locked PRESERVAN su monto guardado; los editables salen del
  // % (round(total*%/100)); el ÚLTIMO tramo EDITABLE absorbe el residuo para que la suma sea EXACTA
  // = monto_hardware. Con 75/25 sobre 13.332.350 → 9.999.263 y 3.333.087.
  const montos = useMemo(() => {
    const lastEditableIdx = tramos.reduce((acc, t, i) => (t.locked ? acc : i), null);
    const base = tramos.map((t) => (t.locked ? redondear(t.preserved?.monto, dec) : null));
    tramos.forEach((t, i) => {
      if (t.locked || i === lastEditableIdx) return;
      base[i] = redondear((montoTotal * (Number(t.pct) || 0)) / 100, dec);
    });
    if (lastEditableIdx != null) {
      const sumOtros = base.reduce((s, m, idx) => (idx === lastEditableIdx ? s : s + (Number(m) || 0)), 0);
      base[lastEditableIdx] = redondear(montoTotal - sumOtros, dec);
    }
    return base;
  }, [tramos, montoTotal, dec]);

  const sumaPct = tramos.reduce((s, t) => s + (Number(t.pct) || 0), 0);
  const pctValido = Math.abs(sumaPct - 100) < 1e-9;
  const montosCuadran = validarSumaCuotas(
    montos.map((m) => ({ monto_cuota: m })),
    montoTotal
  );
  const hayEditable = tramos.some((t) => !t.locked);
  // Cada tramo editable necesita UNA fecha: la real de factura (`fecha`) o, si aún está PENDIENTE,
  // la estimada de facturación (`fechaEstimada`). Sin ninguna de las dos no se puede ubicar en un mes.
  const editablesConFecha = tramos.every((t) => t.locked || !!t.fecha || !!t.fechaEstimada);
  const puedeGuardar =
    !cargando && !guardando && !incompatible && hayEditable && pctValido && montosCuadran && editablesConFecha;

  const guardar = async () => {
    if (!puedeGuardar) return;
    setGuardando(true);
    // L6 tramo 3: por RPC (upsert_linea_facturacion), sin reconstruir el array por índice. Tramos
    // locked NO se tocan (ya están correctos en DB, inmodificables desde este modal). Para un tramo
    // editable: si YA existe como línea (t.clave, resuelto en la precarga), se ubica por esa clave —
    // así una edición nunca "pierde" la identidad de la línea aunque cambie de mes al pasar de
    // estimada a real (el trigger L1 la re-deriva a la clave nueva; sin puente, no hay candado que
    // lo impida). Si es un tramo genuinamente nuevo (sin línea todavía), la clave sale de su propia
    // fecha (real si existe, si no la estimada — misma prioridad que `derivarClaveHardware`).
    try {
      for (let i = 0; i < tramos.length; i++) {
        const t = tramos[i];
        if (t.locked && t.preserved) continue; // ya correcto en DB, no se toca

        const fechaReal = fechaISOLocal(t.fecha);
        const fechaEst = fechaISOLocal(t.fechaEstimada);
        const clave = t.clave; // {ano, mes, tramo} si ya existe como línea (precarga); si no, null

        let ano;
        let mes;
        let tramoNum;
        if (clave) {
          ({ ano, mes, tramo: tramoNum } = clave);
        } else {
          const fechaBase = fechaReal || fechaEst;
          if (!fechaBase) {
            throw new Error(`El tramo ${i + 1} no tiene fecha (real o estimada) — no se puede guardar.`);
          }
          ano = fechaBase.slice(0, 4);
          mes = fechaBase.slice(5, 7);
          tramoNum = 1; // este modal maneja un tramo por mes (el split fijo 2-tramos-por-%, D2 no aplica)
        }

        const campos = { monto: montos[i], porcentaje: Number(t.pct) };
        if (fechaReal) {
          // Fecha real de factura: manda sobre la estimada (misma prioridad que derivarClaveHardware).
          campos.facturafecha = fechaReal;
        } else if (fechaEst) {
          // Sin factura real: `fecha_factura` es el campo que nc_lineas_derivadas usa como fallback de
          // clave (COALESCE(facturafecha, fecha_factura)) — así el tramo "pendiente" SÍ deriva línea.
          campos.fecha_factura = fechaEst;
        }
        if (fechaEst) campos.fecha_factura_estimada = fechaEst; // display propio de este modal
        // Preservar el override de COBRO de Caja (10.143 — la facturación no lo pisa ni lo mezcla).
        if (t.preserved?.fecha_cobro_estimada) campos.fecha_cobro_estimada = t.preserved.fecha_cobro_estimada;

        const { error: rpcErr } = await client.rpc("upsert_linea_facturacion", {
          p_negocio_id: String(negocio.id),
          p_tipo: "hardware",
          p_ano: ano,
          p_mes: mes,
          p_tramo: tramoNum,
          p_campos: campos,
          p_usuario_id: parseInt(userId, 10) || null,
        });
        if (rpcErr) throw rpcErr;
      }
      toast.success("Split de facturación de hardware guardado");
      if (onSaved) onSaved();
    } catch (err) {
      toast.error("Error al guardar el split: " + (err?.message || "error"));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Facturación hardware — split en 2 tramos</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <p className="mb-3">
              <strong>Total hardware:</strong> {formatAmount(montoTotal, moneda)}
            </p>

            {incompatible && (
              <div className="alert alert-warning" role="alert">
                {incompatible}
              </div>
            )}

            {cargando ? (
              <div className="text-muted">Cargando…</div>
            ) : (
              <table className={`table table-sm align-middle ${styles.tabla}`}>
                <thead>
                  <tr>
                    <th>Tramo</th>
                    <th>%</th>
                    <th>Fecha factura</th>
                    <th>Fecha estimada facturación</th>
                    <th style={{ textAlign: "right" }}>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {tramos.map((t, i) => (
                    <tr key={i}>
                      <td>
                        Tramo {i + 1}
                        {t.locked && (
                          <span className="badge bg-secondary ms-1" title="Ya facturado — no editable">
                            facturado
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="input-group input-group-sm">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={t.pct}
                            min={0}
                            max={100}
                            step="any"
                            disabled={t.locked || !!incompatible}
                            onChange={(e) => setPct(i, e.target.value)}
                          />
                          <span className="input-group-text">%</span>
                        </div>
                      </td>
                      <td>
                        <DatePicker
                          selected={t.fecha}
                          onChange={(date) => setFecha(i, date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control form-control-sm"
                          placeholderText="dd/mm/aaaa"
                          isClearable
                          disabled={t.locked || !!incompatible}
                        />
                      </td>
                      <td>
                        {/* Solo para tramo PENDIENTE (sin factura real). Si ya hay `fecha`, esta queda inerte. */}
                        <DatePicker
                          selected={t.fechaEstimada}
                          onChange={(date) => setFechaEstimada(i, date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control form-control-sm"
                          placeholderText={t.fecha ? "—" : "estimada"}
                          isClearable
                          disabled={t.locked || !!incompatible || !!t.fecha}
                        />
                      </td>
                      <td style={{ textAlign: "right", fontWeight: "bold" }}>
                        {formatAmount(incompatible ? t.preserved?.monto || 0 : montos[i], moneda)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!cargando && !incompatible && (
              <div className="mt-2">
                <strong>Suma %:</strong> {sumaPct}
                {!pctValido && <span className="text-danger ms-2">(la suma de los porcentajes debe ser 100)</span>}
                {pctValido && !montosCuadran && (
                  <span className="text-danger ms-2">(los montos no cuadran con el total)</span>
                )}
                {pctValido && montosCuadran && !editablesConFecha && (
                  <span className="text-danger ms-2">
                    (cada tramo editable necesita fecha de factura real o, si está pendiente, fecha estimada)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn btn-primary" disabled={!puedeGuardar} onClick={guardar}>
              {guardando ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturacionHardwareModal;
